import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { pdf } from '@react-pdf/renderer';
import ResumePDF from '../components/resume/ResumePDF';
import { API_ENDPOINTS } from '../config/api';

const ResumeContainer = styled.div`
  padding: 6rem 2rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const DownloadButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: ${props => props.theme.primary};
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  text-decoration: none;
  font-weight: 500;
  transition: background-color 0.3s ease;
  cursor: pointer;

  &:hover {
    background-color: ${props => props.theme.primary}dd;
  }

  svg {
    width: 20px;
    height: 20px;
  }
`;

const Section = styled(motion.section)`
  margin-bottom: 3rem;
  background: ${props => props.theme.surface};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px ${props => props.theme.border};
`;

const SectionTitle = styled.h2`
  color: ${props => props.theme.surfaceText};
  margin-bottom: 1.5rem;
  border-bottom: 2px solid ${props => props.theme.primary};
  padding-bottom: 0.5rem;
`;

const Resume = () => {
  const [resumeData, setResumeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchResumeData = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.RESUME);
        if (!response.ok) {
          throw new Error('Failed to fetch resume data');
        }
        const data = await response.json();
        setResumeData(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchResumeData();
  }, []);

  const handleDownloadPDF = async () => {
    try {
      const blob = await pdf(<ResumePDF data={resumeData} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'naval-prabhakar-resume.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error generating PDF:', err);
    }
  };

  if (loading) {
    return (
      <ResumeContainer>
        <p>Loading resume...</p>
      </ResumeContainer>
    );
  }

  if (error) {
    return (
      <ResumeContainer>
        <p>Error: {error}</p>
      </ResumeContainer>
    );
  }

  if (!resumeData) {
    return (
      <ResumeContainer>
        <p>No resume data available.</p>
      </ResumeContainer>
    );
  }

  return (
    <ResumeContainer>
      <HeaderSection>
        <h1>Resume</h1>
        <DownloadButton
          onClick={handleDownloadPDF}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download PDF
        </DownloadButton>
      </HeaderSection>

      <Section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <SectionTitle>Education</SectionTitle>
        {resumeData.education.map((edu, index) => (
          <div key={index}>
            <h3>{edu.institution}</h3>
            <p>{edu.degree} in {edu.field}</p>
            <p>{edu.year}</p>
          </div>
        ))}
      </Section>

      <Section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <SectionTitle>Experience</SectionTitle>
        {resumeData.experience.map((exp, index) => (
          <div key={index}>
            <h3>{exp.company}</h3>
            <p>{exp.role}</p>
            <p>{exp.period}</p>
            <ul>
              {exp.description.map((desc, i) => (
                <li key={i}>{desc}</li>
              ))}
            </ul>
          </div>
        ))}
      </Section>

      <Section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.4 }}
      >
        <SectionTitle>Skills</SectionTitle>
        {resumeData.skills.map((skill, index) => (
          <div key={index}>
            <h3>{skill.category}</h3>
            <p>{skill.items.join(', ')}</p>
          </div>
        ))}
      </Section>

      <Section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.6 }}
      >
        <SectionTitle>Certifications</SectionTitle>
        <ul>
          {resumeData.certifications.map((cert, index) => (
            <li key={index}>{cert}</li>
          ))}
        </ul>
      </Section>
    </ResumeContainer>
  );
};

export default Resume;
